import { decodeJwt } from 'jose';

const TOKEN_KEY = 'gh_root_auth_tokens';
const PKCE_VERIFIER_KEY = 'gh_root_pkce_verifier';
const LOGIN_STATE_KEY = 'gh_root_login_state';
const RETURN_TO_KEY = 'gh_root_return_to';

interface StoredTokens {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  expiresAt: number;
}

function requireEnv(name: string): string {
  const value = import.meta.env[name];
  if (!value) {
    throw new Error(`Missing required env var: ${name}`);
  }
  return value as string;
}

export function getAuthConfig() {
  const domain = requireEnv('VITE_COGNITO_DOMAIN').replace(/\/+$/, '');
  const clientId = requireEnv('VITE_COGNITO_CLIENT_ID');
  const region = (import.meta.env.VITE_COGNITO_REGION as string) || 'us-east-1';
  const userPoolId = requireEnv('VITE_COGNITO_USER_POOL_ID');

  return {
    domain,
    clientId,
    region,
    userPoolId,
    issuer: `https://cognito-idp.${region}.amazonaws.com/${userPoolId}`,
  };
}

function toBase64Url(input: Uint8Array): string {
  const raw = btoa(String.fromCharCode(...input));
  return raw.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function randomBase64Url(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return toBase64Url(bytes);
}

async function createCodeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier)
  );
  return toBase64Url(new Uint8Array(digest));
}

export function getStoredTokens(): StoredTokens | null {
  const raw = localStorage.getItem(TOKEN_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredTokens;
    if (!parsed.idToken || !parsed.accessToken || !parsed.expiresAt) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearStoredTokens() {
  localStorage.removeItem(TOKEN_KEY);
}

export function isAuthenticated(): boolean {
  const tokens = getStoredTokens();
  if (!tokens) return false;
  return tokens.expiresAt > Date.now() + 30 * 1000;
}

export function getCurrentUserEmail(): string | null {
  const tokens = getStoredTokens();
  if (!tokens) return null;
  try {
    const payload = decodeJwt(tokens.idToken);
    return (payload.email as string) || null;
  } catch {
    return null;
  }
}

export async function beginLogin(returnTo: string) {
  const { domain, clientId } = getAuthConfig();
  const state = randomBase64Url(16);
  const verifier = randomBase64Url(32);
  const challenge = await createCodeChallenge(verifier);
  const callbackUrl = `${window.location.origin}/auth/callback`;

  sessionStorage.setItem(LOGIN_STATE_KEY, state);
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  sessionStorage.setItem(RETURN_TO_KEY, returnTo);

  const authorizeUrl = new URL(`${domain}/oauth2/authorize`);
  authorizeUrl.searchParams.set('client_id', clientId);
  authorizeUrl.searchParams.set('response_type', 'code');
  authorizeUrl.searchParams.set('scope', 'openid');
  authorizeUrl.searchParams.set('redirect_uri', callbackUrl);
  authorizeUrl.searchParams.set('state', state);
  authorizeUrl.searchParams.set('code_challenge', challenge);
  authorizeUrl.searchParams.set('code_challenge_method', 'S256');

  window.location.assign(authorizeUrl.toString());
}

export async function completeLoginFromCallback(search: string): Promise<string> {
  const params = new URLSearchParams(search);
  const code = params.get('code');
  const state = params.get('state');
  const error = params.get('error_description') || params.get('error');
  if (error) {
    throw new Error(error);
  }

  const expectedState = sessionStorage.getItem(LOGIN_STATE_KEY);
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  const returnTo = sessionStorage.getItem(RETURN_TO_KEY) || '/';
  if (!code || !state || !expectedState || state !== expectedState || !verifier) {
    throw new Error('Invalid login callback state');
  }

  const { domain, clientId } = getAuthConfig();
  const callbackUrl = `${window.location.origin}/auth/callback`;
  const body = new URLSearchParams();
  body.set('grant_type', 'authorization_code');
  body.set('client_id', clientId);
  body.set('code', code);
  body.set('redirect_uri', callbackUrl);
  body.set('code_verifier', verifier);

  const res = await fetch(`${domain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!res.ok) {
    throw new Error(`Token exchange failed (${res.status})`);
  }

  const tokenResponse = (await res.json()) as {
    id_token: string;
    access_token: string;
    refresh_token?: string;
    expires_in: number;
  };

  const expiresAt = Date.now() + tokenResponse.expires_in * 1000;
  const tokens: StoredTokens = {
    idToken: tokenResponse.id_token,
    accessToken: tokenResponse.access_token,
    refreshToken: tokenResponse.refresh_token,
    expiresAt,
  };
  localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
  sessionStorage.removeItem(LOGIN_STATE_KEY);
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(RETURN_TO_KEY);

  return returnTo;
}

export function logout() {
  clearStoredTokens();
  const { domain, clientId } = getAuthConfig();
  const logoutUrl = new URL(`${domain}/logout`);
  logoutUrl.searchParams.set('client_id', clientId);
  logoutUrl.searchParams.set('logout_uri', `${window.location.origin}/`);
  window.location.assign(logoutUrl.toString());
}

